import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import useFetch from '../../helpers/useFetch';
import activateAbandonedBrowsingWorkflow from './activateWorkflows/activateAbandonedBrowsingWorkflow';
import activateAbandonedCartWorkflow from './activateWorkflows/activateAbandonedCartWorkflow';
import exploreEmailAnalytics from './analytics/exploreEmailAnalytics';
import exploreMarketingChannelsTab from './analytics/exploreMarketingChannelsTab';
import exploreRecommenderAnalytics from './analytics/exploreRecommenderAnalytics';
import exploreRevenueTab from './analytics/exploreRevenueTab';
import exploreTrafficTab from './analytics/exploreTrafficTab';
import exploreUserBaseTab from './analytics/exploreUserBaseTab';
import exportAnalytics from './analytics/exportAnalytics';
import confirmBrand from './brandIdentity/confirmBrand';
import createNewSegment from './createSegment/createNewSegment';
import exploreSegment from './createSegment/exploreSegment';
import exportSegment from './createSegment/exportSegment';
import createSmsAndViber from './exploreThePlatform/createSmsAndViber';
import exploreProducts from './exploreThePlatform/exploreProducts';
import exploreProfiles from './exploreThePlatform/exploreProfiles';
import workflowsPreview from './exploreThePlatform/workflowsPreview';
import editSubscriptionBanner from './firstBanner/editSubscriptionBanner';
import turnOffSubscriptionBanner from './firstBanner/turnOffSubscriptionBanner';
import turnOnSubscriptionBanner from './firstBanner/turnOnSubscriptionBanner';
import firstOneOffEmail from './firstCampaign/firstOneOffEmail';
import createRecommender from './firstRecommender/createRecommender';
import turnOnRecommender from './firstRecommender/turnOnRecommender';
import addDNSRecords from './gettingStarted/addDNSRecords';
import importUserList from './gettingStarted/importUserList';
import inviteCoWorker from './gettingStarted/inviteCoWorker';

const useGuide = () => {
  const fetch = useFetch();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);

  const [verificationStatus, setVerificationStatus] = useState();
  const [error, setError] = useState();
  const [product, setProduct] = useState();
  const [profile, setProfile] = useState();
  const [cartAbandonmentEmail, setCartAbandonmentEmail] = useState();
  const [cartAbandonmentWorkflow, setCartAbandonmentWorkflow] = useState();
  const [browseAbandonmentEmail, setBrowseAbandonmentEmail] = useState();
  const [browseAbandonmentWorkflow, setBrowseAbandonmentWorkflow] = useState();
  const [recommender, setRecommender] = useState();
  const [segment, setSegment] = useState();
  const [segmentEveryone, setSegmentEveryone] = useState();
  const [imports, setImports] = useState();
  const [campaign, setCampaign] = useState();

  const navigate = useNavigate();

  useEffect(() => {
    if (domain?.id === undefined || domain?.accountId === undefined) {
      return;
    }

    const apiBaseUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}`;

    const fetchRequests = [
      fetch(`${apiBaseUrl}/verificationStatus`),
      fetch(`${apiBaseUrl}/imports`),
      fetch(`${apiBaseUrl}/products?from=0&size=1`),
      fetch(`${apiBaseUrl}/profiles?showAnonymous=false&from=0&size=1`),
      fetch(`${apiBaseUrl}/emails?name=[Automated]%20Cart%20abandonment`),
      fetch(`${apiBaseUrl}/campaigns?name=Cart%20abandonment`),
      fetch(`${apiBaseUrl}/emails?name=[Automated]%20Browse%20abandonment`),
      fetch(`${apiBaseUrl}/campaigns?name=Browse%20abandonment`),
      fetch(`${apiBaseUrl}/segments?name=Sent%20email%20from%20campaign%20or%20workflow`),
      fetch(`${apiBaseUrl}/segments?name=Everyone`),
      fetch(`${apiBaseUrl}/campaigns`),
      fetch(`${apiBaseUrl}/pages`),
    ];

    // One failing request (e.g. verificationStatus) must not hide the whole guide.
    Promise.allSettled(fetchRequests)
      .then((results) => results.map((result) => (result.status === 'fulfilled' ? result.value : null)))
      .then(
        ([
          verificationStatus,
          imports,
          products,
          profiles,
          cartAbandonmentEmails,
          cartAbandonmentWorkflows,
          browseAbandonmentEmails,
          browseAbandonmentWorkflows,
          segments,
          segmentsEveryone,
          campaigns,
          pages,
        ]) => {
          setVerificationStatus(verificationStatus);
          setImports(imports?.imports);
          setProduct(products?.products[0]);
          setProfile(profiles?.profiles[0]);
          setCartAbandonmentEmail(cartAbandonmentEmails?.emails[0]);
          setCartAbandonmentWorkflow(cartAbandonmentWorkflows?.campaigns[0]);
          setBrowseAbandonmentEmail(browseAbandonmentEmails?.emails[0]);
          setBrowseAbandonmentWorkflow(browseAbandonmentWorkflows?.campaigns[0]);
          setSegment(segments?.segments[0]);
          setSegmentEveryone(segmentsEveryone?.segments[0]);
          setCampaign(campaigns?.campaigns[0]);
          setRecommender(
            pages?.pages
              .find((page) => page.name === 'Homepage')
              ?.recommenders?.find((r) => r.name === 'Recommended for You')
          );
        }
      )
      .catch(setError);
  }, [domain?.accountId, domain?.id, fetch, dispatch]);

  if (!domain) {
    return;
  }

  const baseUrl = `/accounts/${domain.accountId}/domains/${domain.id}`;

  if (error) {
    console.error(error);
    return;
  }

  return {
    tabs: [
      {
        id: 'gettingStarted',
        title: 'Getting Started',
        tasks: [
          addDNSRecords(baseUrl, navigate, verificationStatus),
          inviteCoWorker(baseUrl, navigate, user),
          importUserList(baseUrl, navigate, imports),
        ],
      },
      {
        id: 'brandIdentity',
        title: 'Brand Identity',
        tasks: [confirmBrand(baseUrl, navigate, domain)],
      },
      {
        id: 'exploreThePlatform',
        title: 'Explore the Platform',
        tasks: [
          ...(profile ? [exploreProfiles(baseUrl, navigate, profile, user)] : []),
          ...(product ? [exploreProducts(baseUrl, navigate, product, user)] : []),
          ...(cartAbandonmentWorkflow ? [workflowsPreview(baseUrl, navigate, cartAbandonmentWorkflow, user)] : []),
          createSmsAndViber(baseUrl, navigate, user),
        ],
      },
      {
        id: 'firstCampaign',
        title: 'Send Your First Campaign',
        tasks: [...(campaign ? [firstOneOffEmail(baseUrl, navigate, user, segmentEveryone)] : [])],
      },
      {
        id: 'activateWorkflows',
        title: 'Activate Workflows',
        tasks: [
          ...(cartAbandonmentEmail && cartAbandonmentWorkflow
            ? [activateAbandonedCartWorkflow(baseUrl, navigate, cartAbandonmentEmail, cartAbandonmentWorkflow)]
            : []),
          ...(browseAbandonmentEmail && browseAbandonmentWorkflow
            ? [activateAbandonedBrowsingWorkflow(baseUrl, navigate, browseAbandonmentEmail, browseAbandonmentWorkflow)]
            : []),
        ],
      },
      {
        id: 'firstBanner',
        title: 'Create Your First Banner Block',
        tasks: [
          editSubscriptionBanner(baseUrl, navigate, domain, user),
          turnOnSubscriptionBanner(baseUrl, navigate, domain, user),
          turnOffSubscriptionBanner(baseUrl, navigate, domain, user),
        ],
      },
      {
        id: 'firstRecommender',
        title: 'Create Your First Product Block',
        tasks: [
          ...(recommender ? [createRecommender(baseUrl, navigate, recommender, user)] : []),
          ...(recommender ? [turnOnRecommender(baseUrl, navigate, recommender)] : []),
        ],
      },
      {
        id: 'createSegment',
        title: 'Create segment',
        tasks: [
          ...(segment ? [exploreSegment(baseUrl, navigate, segment, user)] : []),
          createNewSegment(baseUrl, navigate, user),
          ...(segmentEveryone ? [exportSegment(baseUrl, navigate, segmentEveryone, user)] : []),
        ],
      },
      {
        id: 'analytics',
        title: 'Analytics & Billing',
        tasks: [
          exploreRevenueTab(baseUrl, navigate, user),
          exploreTrafficTab(baseUrl, navigate, user),
          exploreUserBaseTab(baseUrl, navigate, user),
          exportAnalytics(baseUrl, navigate, user),
          exploreRecommenderAnalytics(baseUrl, navigate, user),
          exploreEmailAnalytics(baseUrl, navigate, user),
          exploreMarketingChannelsTab(baseUrl, navigate, user),
        ].filter(Boolean),
      },
    ],
  };
};

export default useGuide;
